import { CalendarDaysIcon, CheckCircleIcon, EnvelopeIcon, UserGroupIcon, UsersIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import {
    UserGroupIcon as PeopleSolid, ViewColumnsIcon as PipelineSolid, CalendarDaysIcon as CalendarSolid, CheckCircleIcon as CheckSolid, EnvelopeIcon as EnvelopeSolid, UsersIcon as UsersSolid
} from '@heroicons/react/24/solid';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RouteConstants } from '../../../route-constants';
import { useSalesAccess } from '../../../services/crm';

type Icon = React.ComponentType<{ className?: string }>;
interface TabDef { key: string; label: string; href: string; icon: Icon; solid: Icon; match: (path: string) => boolean; managerOnly?: boolean }

const TABS: TabDef[] = [
    { key: 'people', label: 'People', href: RouteConstants.SALES_INBOX, icon: UserGroupIcon, solid: PeopleSolid, match: (p) => p.startsWith('/sales/inbox') || p.startsWith('/sales/leads') },
    { key: 'pipeline', label: 'Pipeline', href: RouteConstants.SALES_PIPELINE, icon: ViewColumnsIcon, solid: PipelineSolid, match: (p) => p.startsWith('/sales/pipeline') },
    { key: 'email', label: 'Email', href: RouteConstants.SALES_MESSAGES, icon: EnvelopeIcon, solid: EnvelopeSolid, match: (p) => p.startsWith('/sales/email') || p.startsWith('/sales/templates') },
    { key: 'calendar', label: 'Calendar', href: RouteConstants.SALES_CALENDAR, icon: CalendarDaysIcon, solid: CalendarSolid, match: (p) => p.startsWith('/sales/calendar') },
    { key: 'tasks', label: 'Tasks', href: RouteConstants.SALES_TASKS, icon: CheckCircleIcon, solid: CheckSolid, match: (p) => p.startsWith('/sales/tasks') },
    { key: 'team', label: 'Team', href: RouteConstants.SALES_TEAM, icon: UsersIcon, solid: UsersSolid, match: (p) => p.startsWith('/sales/team'), managerOnly: true }
];

/** Height the pages pad for, and the lead page's action bar sits above. */
export const SALES_TAB_BAR_HEIGHT = '3.75rem';

// #region SalesTabBar
/**
 * The phone's way around Sales: a bottom tab bar, the way every native app
 * does it. Rendered once by the sales access wrapper, so pages don't know it
 * exists; a lead page lights the People tab because that's where it came from.
 */
export const SalesTabBar: React.FunctionComponent<{}> = (): React.ReactElement => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [{ isManager }] = useSalesAccess();
    const tabs = TABS.filter((t) => !t.managerOnly || isManager);

    return (
        <nav
            aria-label="Sales"
            className="fixed inset-x-0 bottom-0 z-40 border-t border-surface-border bg-surface-raised/95 backdrop-blur"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
            <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`, height: SALES_TAB_BAR_HEIGHT }}>
                {tabs.map((t) => {
                    const active = t.match(pathname);
                    const I = active ? t.solid : t.icon;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => { if (!active || pathname !== t.href) navigate(t.href); }}
                            aria-current={active ? 'page' : undefined}
                            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition ${active ? 'text-emerald-700 dark:text-emerald-300' : 'text-zinc-500 dark:text-zinc-400'}`}
                        >
                            <I className="h-6 w-6" />
                            {t.label}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};
